import moment from 'moment';
import { theme } from 'theme/theme';
import { TIME_FORMAT } from '../../common/constants';
import { convertToBST } from '../../../src/common/dates';

export const REPORT_STATUSES = {
    IN_SERVICE: 'MANUAL_CONTROL',
    WORKING: 'WORKING',
    STOPPED: 'STOPPED',
    ALARM_RUNNING: 'ALARM_RUNNING',
    AVAILABLE: 'AVAILABLE',
    CURTAILED: 'CURTAILED',
    NO_COMM: 'NO_COMM',
    STOPPED_MANUAL: 'STOPPED_MANUAL',
    ALARM_STOPPED: 'ALARM_STOPPED',
    FAULT: 'FAULT',
    DURATION: 'DURATION',
    UNAVAILABLE: 'UNAVAILABLE',
};

export const RELEASES = {
    HV: 'H',
    WT: 'R',
};

export const TYPES = { ...REPORT_STATUSES, ...RELEASES };

export const STATUS_COLOR_MAP = {
    [REPORT_STATUSES.IN_SERVICE]: theme.color.yellow,
    [REPORT_STATUSES.STOPPED]: theme.color.brown,
    [REPORT_STATUSES.ALARM_RUNNING]: theme.color.orange,
    [REPORT_STATUSES.AVAILABLE]: theme.color.green,
    [REPORT_STATUSES.CURTAILED]: theme.color.blue,
    [REPORT_STATUSES.NO_COMM]: theme.color.red,
    [REPORT_STATUSES.STOPPED_MANUAL]: theme.color.pink,
    [REPORT_STATUSES.ALARM_STOPPED]: theme.color.brownDark,
    [REPORT_STATUSES.FAULT]: theme.color.grayDark3,
    [REPORT_STATUSES.WORKING]: theme.color.grayDark,
    [REPORT_STATUSES.DURATION]: theme.color.white,
    [REPORT_STATUSES.UNAVAILABLE]: [theme.color.black, theme.color.gray],
};

export const getStatusFillColor = (type) =>
    Array.isArray(STATUS_COLOR_MAP[type]) ? STATUS_COLOR_MAP[type][0] : STATUS_COLOR_MAP[type];

export const getStatusStrokeColor = (type) =>
    Array.isArray(STATUS_COLOR_MAP[type]) ? STATUS_COLOR_MAP[type][1] : STATUS_COLOR_MAP[type];

export const RELEASE_COLOR_MAP = {
    [RELEASES.HV]: theme.color.white,
    [RELEASES.WT]: theme.color.yellow,
};

export const COLORS = { ...STATUS_COLOR_MAP, ...RELEASE_COLOR_MAP };

export const isOddHour = (date) => convertToBST(date).get('hour') % 2;

export const getEarliestBeginDate = (selectedDate) =>
    convertToBST(selectedDate)
        .startOf('day')
        .valueOf();

export const getLatestEndDate = (selectedDate) =>
    convertToBST(selectedDate)
        .endOf('day')
        .valueOf();

export const getBeginDate = (d) => (d.beginDate ? convertToBST(d.beginDate) : null);

export const getEndDate = ({ beginDate, endDate }) => {
    const minMilliseconds = 400000;
    if (!endDate || !beginDate) return null;
    return convertToBST(endDate - beginDate < minMilliseconds ? endDate + minMilliseconds : endDate);
};

export const ticksEveryOtherHourIncludingLast = (timeFormat = TIME_FORMAT) => (date) => {
    return isOddHour(date) ? null : convertToBST(date).format(timeFormat);
};

export const ticksEveryOtherHour = (timeFormat = TIME_FORMAT) => (date, index, ticks) => {
    return isOddHour(date) || index === ticks.length - 1 ? null : convertToBST(date).format(timeFormat);
};

export const ticksEveryMonth = (timeFormat = 'MMMM') => (date) => {
    return convertToBST(date).format(timeFormat);
};

export const generateTickValues = (date) => {
    const ticks = 25;

    const day = convertToBST(date).startOf('day');
    const dates = [...Array(ticks)].map((_, i) => {
        return day.clone().add(i, 'hours');
    });

    return dates;
};

export const monthDiff = (dateFrom, dateTo) => {
    return Math.round(dateTo.diff(dateFrom, 'months', true));
};

export const generateMonthlyTickValues = (dateStart, dateEnd) => {
    const ticks = monthDiff(dateStart, dateEnd);
    const day = convertToBST(dateStart).startOf('month');
    const dates = [...Array(ticks)].map((_, i) => {
        return day.clone().add(i, 'months');
    });

    return dates;
};

export const getTimeAxisTickValues = (arr) => generateTickValues(arr[0] ? arr[0].key : new Date());

export const getDomainForDay = (date) => {
    // There are two types of input. from date (eg: timelineData[0].beginDate) or from datepicker(selecedDate)
    // Currently selectedDate is in users timezone, show we shift it here:

    const utcTime = moment.isMoment(date)
        ? moment(date)
              .utc()
              .add(date.utcOffset(), 'm')
        : date;

    const domain = [
        convertToBST(utcTime)
            .startOf('day')
            .valueOf(),

        convertToBST(utcTime)
            .startOf('day')
            .add(1, 'days')
            .valueOf(),
    ];

    return domain;
};

export const getDomainForPeriod = (dateStart, dateEnd) => {
    // There are two types of input. from date (eg: timelineData[0].beginDate) or from datepicker(selecedDate)
    // Currently selectedDate is in users timezone, show we shift it here:

    const utcTimeStart = moment.isMoment(dateStart)
        ? moment(dateStart)
              .utc()
              .add(dateStart.utcOffset(), 'm')
        : dateStart;

    const utcTimeEnd = moment.isMoment(dateEnd)
        ? moment(dateEnd)
              .utc()
              .add(dateEnd.utcOffset(), 'm')
        : dateEnd;

    const domain = [
        convertToBST(utcTimeStart)
            .startOf('day')
            .toDate(),

        convertToBST(utcTimeEnd)
            .endOf('day')
            .toDate(),
    ];

    return domain;
};

export const timelineFilters = [
    {
        name: REPORT_STATUSES.AVAILABLE,
        value: 'Available',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.AVAILABLE],
    },
    {
        name: REPORT_STATUSES.IN_SERVICE,
        value: 'In Service',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.IN_SERVICE],
    },
    /* Currently not supported. See POW-58
    {
        name: REPORT_STATUSES.STOPPED,
        value: 'Stopped',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.STOPPED],
    },
    */
    {
        name: REPORT_STATUSES.ALARM_RUNNING,
        value: 'Alarm: Running',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.ALARM_RUNNING],
    },
    {
        name: REPORT_STATUSES.CURTAILED,
        value: 'Curtailed',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.CURTAILED],
    },
    {
        name: REPORT_STATUSES.NO_COMM,
        value: 'No comms',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.NO_COMM],
    },
    /* Currently not supported. See POW-58
    {
        name: REPORT_STATUSES.STOPPED_MANUAL,
        value: 'Stopped: Manual',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.STOPPED_MANUAL],
    },
    */
    {
        name: REPORT_STATUSES.ALARM_STOPPED,
        value: 'Alarm: Stopped',
        borderColor: STATUS_COLOR_MAP[REPORT_STATUSES.ALARM_STOPPED],
    },
];
